// Plan Q&A Integration Service
// Service for integrating Plan Q&A functionality with existing conversations

import {
  getFirestore,
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
  query,
  where,
  orderBy,
  Timestamp,
  Firestore
} from 'firebase/firestore';
import { getAuth, User } from 'firebase/auth';
import {
  PlanQaMessage,
  CreatePlanQaMessageData,
  PlanQaError,
  PlanQaErrorCodes,
  EnhancedUserPlan
} from '../types/planQa';

export class PlanQaIntegrationService {
  private db: Firestore;
  private auth: ReturnType<typeof getAuth>;

  constructor() {
    this.db = getFirestore();
    this.auth = getAuth();
  }

  /**
   * Get the current authenticated user
   */
  private getCurrentUser(): User {
    const user = this.auth.currentUser;
    if (!user) {
      console.error('❌ Authentication Error: No current user found');
      throw new PlanQaError(
        'User not authenticated',
        PlanQaErrorCodes.AUTHENTICATION_REQUIRED
      );
    }
    
    if (!user.uid) {
      console.error('❌ Authentication Error: User UID is missing');
      throw new PlanQaError(
        'User authentication incomplete',
        PlanQaErrorCodes.AUTHENTICATION_REQUIRED
      );
    }
    
    console.log('✅ Authentication validated:', {
      uid: user.uid,
      email: user.email,
      isAnonymous: user.isAnonymous
    });
    
    return user;
  }

  /**
   * Add a Plan Q&A message to an existing conversation
   */
  async addPlanQaMessage(
    conversationId: string,
    messageData: CreatePlanQaMessageData
  ): Promise<void> {
    try {
      const user = this.getCurrentUser();
      
      console.log('🔧 Adding Plan Q&A message to conversation:', {
        conversationId,
        planId: messageData.planContext.planId,
        day: messageData.planContext.day,
        contextType: messageData.planContext.contextType
      });

      // Add message to the existing conversation
      const messageRef = collection(
        doc(this.db, 'conversations', conversationId),
        'messages'
      );

      const fullMessageData: Omit<PlanQaMessage, 'id'> = {
        ...messageData,
        userId: messageData.role === 'assistant' ? 'assistant' : user.uid,
        createdAt: Timestamp.now(),
        conversationId
      };

      await addDoc(messageRef, fullMessageData);

      // Update conversation last updated time
      const conversationRef = doc(this.db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        lastUpdated: Timestamp.now()
      });

      // Update plan Q&A usage tracking
      await this.updatePlanQaUsageTracking(
        messageData.planContext.planId,
        messageData.planContext.day,
        conversationId,
        user.uid
      );

      console.log('✅ Plan Q&A message added successfully');
    } catch (error) {
      console.error('❌ Error adding Plan Q&A message:', error);
      throw new PlanQaError(
        'Failed to add Plan Q&A message',
        PlanQaErrorCodes.DATABASE_ERROR,
        error as Error
      );
    }
  }

  /**
   * Get all Plan Q&A messages for a plan across all conversations and days
   */
  async getAllPlanQaMessages(planId: string): Promise<PlanQaMessage[]> {
    try {
      const user = this.getCurrentUser();
      
      console.log('🔍 Getting all Plan Q&A messages for plan:', planId);

      // Get all conversations that contain Plan Q&A for this plan
      const conversationIds = await this.getAllConversationsWithPlanQa(planId);
      
      if (conversationIds.length === 0) {
        console.log('📝 No conversations found with Plan Q&A for this plan');
        return [];
      }

      const allMessages: PlanQaMessage[] = [];

      // Get messages from each conversation
      for (const conversationId of conversationIds) {
        const messages = await this.getPlanQaMessages(conversationId, planId);
        allMessages.push(...messages);
      }

      // Sort messages by creation time
      allMessages.sort((a, b) => {
        const timeA = a.createdAt instanceof Date ? a.createdAt.getTime() : a.createdAt.toDate().getTime();
        const timeB = b.createdAt instanceof Date ? b.createdAt.getTime() : b.createdAt.toDate().getTime();
        return timeA - timeB;
      });

      console.log('✅ Retrieved all Plan Q&A messages:', allMessages.length);
      return allMessages;
    } catch (error) {
      console.error('❌ Error getting all Plan Q&A messages:', error);
      throw new PlanQaError(
        'Failed to get all Plan Q&A messages',
        PlanQaErrorCodes.DATABASE_ERROR
      );
    }
  }

  /**
   * Get all Plan Q&A messages from a conversation
   */
  async getPlanQaMessages(
    conversationId: string,
    planId?: string,
    day?: number
  ): Promise<PlanQaMessage[]> {
    try {
      const user = this.getCurrentUser();
      
      console.log('🔍 Getting Plan Q&A messages:', {
        conversationId,
        planId,
        day
      });

      const messagesRef = collection(
        doc(this.db, 'conversations', conversationId),
        'messages'
      );

      // Try different query approaches based on what parameters we have
      let messages: PlanQaMessage[] = [];
      
      if (planId && day) {
        // Specific plan and day query
        try {
          const q = query(
            messagesRef,
            where('planContext.planId', '==', planId),
            where('planContext.day', '==', day),
            orderBy('createdAt', 'asc')
          );
          const snapshot = await getDocs(q);
          messages = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as PlanQaMessage[];
          console.log(`✅ Found ${messages.length} messages for plan ${planId}, day ${day}`);
        } catch (indexError) {
          console.log('Index query failed, trying fallback approach:', indexError);
          // Fallback: get all messages and filter in memory
          const allSnapshot = await getDocs(query(messagesRef, orderBy('createdAt', 'asc')));
          const allMessages = allSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as PlanQaMessage[];
          messages = allMessages.filter(msg => 
            msg.planContext?.planId === planId && msg.planContext?.day === day
          );
          console.log(`✅ Found ${messages.length} messages (fallback) for plan ${planId}, day ${day}`);
        }
      } else if (planId) {
        // Plan-specific query (all days)
        try {
          const q = query(
            messagesRef,
            where('planContext.planId', '==', planId),
            orderBy('createdAt', 'asc')
          );
          const snapshot = await getDocs(q);
          messages = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as PlanQaMessage[];
          console.log(`✅ Found ${messages.length} messages for plan ${planId} (all days)`);
        } catch (indexError) {
          console.log('Index query failed, trying fallback approach:', indexError);
          // Fallback: get all messages and filter in memory
          const allSnapshot = await getDocs(query(messagesRef, orderBy('createdAt', 'asc')));
          const allMessages = allSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as PlanQaMessage[];
          messages = allMessages.filter(msg => msg.planContext?.planId === planId);
          console.log(`✅ Found ${messages.length} messages (fallback) for plan ${planId} (all days)`);
        }
      } else {
        // Get all messages and filter for plan context
        const allSnapshot = await getDocs(query(messagesRef, orderBy('createdAt', 'asc')));
        const allMessages = allSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as PlanQaMessage[];
        messages = allMessages.filter(msg => msg.planContext);
        console.log(`✅ Found ${messages.length} messages with plan context`);
      }

      // Additional filtering to ensure we only return Plan Q&A messages
      const planQaMessages = messages.filter(msg => msg.planContext);
      
      console.log(`✅ Final result: ${planQaMessages.length} Plan Q&A messages`);
      return planQaMessages;
    } catch (error) {
      console.error('❌ Error getting Plan Q&A messages:', error);
      throw new PlanQaError(
        'Failed to retrieve Plan Q&A messages',
        PlanQaErrorCodes.DATABASE_ERROR,
        error as Error
      );
    }
  }

  /**
   * Get all conversations that contain Plan Q&A for a specific plan (all days)
   */
  async getAllConversationsWithPlanQa(planId: string): Promise<string[]> {
    try {
      const user = this.getCurrentUser();
      
      console.log('🔍 Getting all conversations with Plan Q&A for plan:', planId);

      // Get all user conversations
      const conversationsRef = collection(this.db, 'conversations');
      const q = query(
        conversationsRef,
        where('userId', '==', user.uid),
        orderBy('lastUpdated', 'desc')
      );

      const snapshot = await getDocs(q);
      const conversationIds: string[] = [];

      // Check each conversation for Plan Q&A messages
      for (const convDoc of snapshot.docs) {
        const messagesRef = collection(
          doc(this.db, 'conversations', convDoc.id),
          'messages'
        );
        
        const messagesQuery = query(
          messagesRef,
          where('planContext.planId', '==', planId)
        );
        
        const messagesSnapshot = await getDocs(messagesQuery);
        
        if (!messagesSnapshot.empty) {
          conversationIds.push(convDoc.id);
        }
      }

      console.log('✅ Found conversations with Plan Q&A:', conversationIds);
      return conversationIds;
    } catch (error) {
      console.error('❌ Error getting conversations with Plan Q&A:', error);
      throw new PlanQaError(
        'Failed to get conversations with Plan Q&A',
        PlanQaErrorCodes.DATABASE_ERROR
      );
    }
  }

  /**
   * Get all conversations that contain Plan Q&A for a specific plan and day
   */
  async getConversationsWithPlanQa(
    planId: string,
    day: number
  ): Promise<string[]> {
    try {
      const user = this.getCurrentUser();
      
      console.log('🔍 Getting conversations with Plan Q&A:', {
        planId,
        day
      });

      // Get all user conversations
      const conversationsRef = collection(this.db, 'conversations');
      const q = query(
        conversationsRef,
        where('userId', '==', user.uid),
        orderBy('lastUpdated', 'desc')
      );

      const snapshot = await getDocs(q);
      const conversationIds: string[] = [];

      // Check each conversation for Plan Q&A messages
      for (const convDoc of snapshot.docs) {
        const messagesRef = collection(
          doc(this.db, 'conversations', convDoc.id),
          'messages'
        );
        
        const messagesQuery = query(
          messagesRef,
          where('planContext.planId', '==', planId),
          where('planContext.day', '==', day)
        );
        
        const messagesSnapshot = await getDocs(messagesQuery);
        if (!messagesSnapshot.empty) {
          conversationIds.push(convDoc.id);
        }
      }

      console.log(`✅ Found ${conversationIds.length} conversations with Plan Q&A`);
      return conversationIds;
    } catch (error) {
      console.error('❌ Error getting conversations with Plan Q&A:', error);
      throw new PlanQaError(
        'Failed to retrieve conversations with Plan Q&A',
        PlanQaErrorCodes.DATABASE_ERROR,
        error as Error
      );
    }
  }

  /**
   * Update plan Q&A usage tracking in the plan document
   */
  private async updatePlanQaUsageTracking(
    planId: string,
    day: number,
    conversationId: string,
    userId: string
  ): Promise<void> {
    try {
      const planRef = doc(
        collection(this.db, 'users'),
        userId,
        'plans',
        planId
      );

      const planDoc = await getDoc(planRef);
      let currentPlan = planDoc.data() as EnhancedUserPlan;

      // If plan document doesn't exist, create it with basic structure
      if (!currentPlan) {
        currentPlan = {
          planId,
          day: 1,
          stickers: 0,
          completedActivities: [],
          lastCompleted: new Date(),
          dailyQaUsage: {}
        };
        await setDoc(planRef, currentPlan);
      }

      const dayKey = day.toString();
      const dailyQaUsage = currentPlan.dailyQaUsage || {};
      const dayData = dailyQaUsage[dayKey] || {
        totalQuestions: 0,
        totalTokensUsed: 0,
        averageResponseTime: 0,
        userSatisfaction: 0,
        lastQaAt: Timestamp.now(),
        conversationIds: []
      };

      dayData.totalQuestions += 1;
      dayData.lastQaAt = Timestamp.now();
      
      // Add conversation ID if not already present
      if (!dayData.conversationIds.includes(conversationId)) {
        dayData.conversationIds.push(conversationId);
      }

      await updateDoc(planRef, {
        [`dailyQaUsage.${dayKey}`]: dayData
      });

      console.log('✅ Plan Q&A usage tracking updated');
    } catch (error) {
      console.error('❌ Error updating plan Q&A usage tracking:', error);
      // Don't throw here as this is not critical for the core functionality
    }
  }

  /**
   * Get plan Q&A statistics for a specific plan
   */
  async getPlanQaStats(planId: string): Promise<{
    totalQuestions: number;
    totalTokensUsed: number;
    averageRating: number;
    questionsByDay: { [day: number]: number };
  }> {
    try {
      const user = this.getCurrentUser();
      
      console.log('📊 Getting Plan Q&A stats for plan:', planId);

      const planRef = doc(
        collection(this.db, 'users'),
        user.uid,
        'plans',
        planId
      );

      const planDoc = await getDoc(planRef);
      if (!planDoc.exists()) {
        return {
          totalQuestions: 0,
          totalTokensUsed: 0,
          averageRating: 0,
          questionsByDay: {}
        };
      }

      const planData = planDoc.data() as EnhancedUserPlan;
      const dailyQaUsage = planData.dailyQaUsage || {};

      let totalQuestions = 0;
      let totalTokensUsed = 0;
      let totalRating = 0;
      let ratingCount = 0;
      const questionsByDay: { [day: number]: number } = {};

      Object.entries(dailyQaUsage).forEach(([dayKey, dayData]) => {
        const day = parseInt(dayKey);
        questionsByDay[day] = dayData.totalQuestions;
        totalQuestions += dayData.totalQuestions;
        totalTokensUsed += dayData.totalTokensUsed;
        
        if (dayData.userSatisfaction > 0) {
          totalRating += dayData.userSatisfaction;
          ratingCount += 1;
        }
      });

      const averageRating = ratingCount > 0 ? totalRating / ratingCount : 0;

      console.log('✅ Plan Q&A stats retrieved:', {
        totalQuestions,
        totalTokensUsed,
        averageRating
      });

      return {
        totalQuestions,
        totalTokensUsed,
        averageRating,
        questionsByDay
      };
    } catch (error) {
      console.error('❌ Error getting Plan Q&A stats:', error);
      throw new PlanQaError(
        'Failed to retrieve Plan Q&A statistics',
        PlanQaErrorCodes.DATABASE_ERROR,
        error as Error
      );
    }
  }
}

// Export singleton instance
export const planQaIntegrationService = new PlanQaIntegrationService();
